import React, { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from "react-leaflet";
import { Crosshair, MapPin } from "lucide-react";
import "leaflet/dist/leaflet.css";
import "../lib/leafletIcon";
import { SUMUT_CENTER, SUMUT_BOUNDS } from "../lib/wilayah";

const COORD_DECIMALS = 6; // ~0,1 meter — cukup untuk geotag lokasi usaha

const round = (n) => Number(Number(n).toFixed(COORD_DECIMALS));

/** Klik di mana saja pada peta memindahkan titik geotag. */
function ClickHandler({ onPick }) {
  useMapEvents({
    click: (e) => onPick(round(e.latlng.lat), round(e.latlng.lng)),
  });
  return null;
}

/** Batas panning mengikuti wilayah: Sumut saja, atau bebas untuk provinsi lain. */
function BoundsUpdater({ restrictToSumut }) {
  const map = useMap();
  useEffect(() => {
    map.setMaxBounds(restrictToSumut ? SUMUT_BOUNDS : null);
    map.setMinZoom(restrictToSumut ? 7 : 3);
  }, [restrictToSumut, map]);
  return null;
}

/**
 * Geser peta hanya jika titik berada di luar tampilan — misalnya saat user
 * mengganti pilihan Kota. Klik di dalam peta tidak menggeser tampilan.
 */
function KeepPointVisible({ lat, lng }) {
  const map = useMap();
  useEffect(() => {
    if (lat == null || lng == null) return;
    if (!map.getBounds().contains([lat, lng])) map.setView([lat, lng], map.getZoom());
  }, [lat, lng, map]);
  return null;
}

export default function LocationPicker({
  lat, lng, onChange, height = 320, restrictToSumut = true,
  hint = "Klik pada peta atau geser penanda untuk menentukan titik lokasi usaha.",
}) {
  const [geoError, setGeoError] = useState("");
  const [locating, setLocating] = useState(false);

  const numLat = Number(lat);
  const numLng = Number(lng);
  const hasPoint = Number.isFinite(numLat) && Number.isFinite(numLng) && lat !== "" && lng !== "";
  const center = hasPoint ? [numLat, numLng] : SUMUT_CENTER;

  const pick = (newLat, newLng) => {
    setGeoError("");
    onChange(newLat, newLng);
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      setGeoError("Perangkat ini tidak mendukung deteksi lokasi.");
      return;
    }
    setGeoError("");
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        pick(round(pos.coords.latitude), round(pos.coords.longitude));
        setLocating(false);
      },
      () => {
        setGeoError("Lokasi tidak dapat diakses. Izinkan akses lokasi pada browser, atau pilih titik langsung pada peta.");
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  return (
    <div data-testid="location-picker">
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <p className="text-xs text-slate-500 flex items-center gap-1.5">
          <MapPin className="w-3.5 h-3.5 text-[#045498]" />
          {hint}
        </p>
        <button
          type="button"
          onClick={useMyLocation}
          disabled={locating}
          className="kedan-btn-secondary !py-1 !px-2.5 text-xs"
          data-testid="location-picker-geolocate"
        >
          <Crosshair className="w-3.5 h-3.5" />
          {locating ? "Mencari lokasi..." : "Gunakan lokasi saya"}
        </button>
      </div>

      <div className="rounded-lg overflow-hidden border border-slate-200" style={{ height }}>
        <MapContainer
          center={center}
          zoom={hasPoint ? 15 : 8}
          minZoom={restrictToSumut ? 7 : 3}
          maxZoom={18}
          maxBounds={restrictToSumut ? SUMUT_BOUNDS : undefined}
          maxBoundsViscosity={1.0}
          style={{ height: "100%", width: "100%" }}
          scrollWheelZoom
        >
          <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <ClickHandler onPick={pick} />
          <BoundsUpdater restrictToSumut={restrictToSumut} />
          <KeepPointVisible lat={hasPoint ? numLat : null} lng={hasPoint ? numLng : null} />
          {hasPoint && (
            <Marker
              position={[numLat, numLng]}
              draggable
              eventHandlers={{
                dragend: (e) => {
                  const { lat: dLat, lng: dLng } = e.target.getLatLng();
                  pick(round(dLat), round(dLng));
                },
              }}
            />
          )}
        </MapContainer>
      </div>

      {geoError && (
        <div className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5"
          data-testid="location-picker-error">
          {geoError}
        </div>
      )}
    </div>
  );
}
